import { useEffect, useState } from 'react'
import { Sheet } from '../ui/Sheet'
import { fetchServers, type PairedServer, type ServerList } from './serverList'
import { CreateProjectSection } from './CreateProjectSection'
import { ServerField } from './ServerField'
import { CreateAgentSection } from './CreateAgentSection'
import { CreateTerminalSection } from './CreateTerminalSection'
import { CreateGroupSection } from '../groups/CreateGroupSection'
import { GamesSection } from '../games/GamesSection'
import { gamesOn } from '../games/flag'
import type { ProjectRow } from '../transport/api'
import { AddHumansLazy as AddHumans } from '../friends/AddHumansLazy'
import { InstallSectionLazy as InstallSection } from '../install/InstallSectionLazy'
import { Icon } from '../ui/Icon'
import { LAUNCHPAD_TILES, type LaunchpadKey } from '../ui/launchpad'

/**
 * THE ＋ OPENS A MENU — owner, 2026-10-17: 使用者按下 + 按鈕之後彈出選單: Add a new machine,
 * Agent, Project, Humans. 如果沒有任何的機器, 那麼 Agent 是 Disable 的狀態. 每一個欄位都會
 * 讓使用者跳到下一個選單 (有 < 按鈕可以回到上一個選單) … 類似 Agent 設定選單的風格.
 *
 * It replaces the tab strip (Agent · Project · Humans) and the Agent tab's two sub-tabs
 * (Select a machine · Add a new machine): one list of rows in the agent card's own style
 * (.ac-menu / .ac-row, the pin sheet reuses it too), each row opening its view in place,
 * with the card's back row (.ac-back) to return. "Add a new machine" is a row of its own
 * now, first, so the first visit — nothing paired — has its obvious next step on top while
 * Agent says why it cannot be chosen yet.
 *
 * Icons and colours of the three that were tabs still come from LAUNCHPAD_TILES, one
 * definition with the launchpad card (owner, 2026-08-18: 我要的只是加入 icon); the
 * machine row has its own.
 */
type View = 'menu' | 'machine' | 'terminal' | 'group' | 'game' | LaunchpadKey

const MACHINE_ROW = { label: 'Add a new machine', icon: 'computer' as const, color: 'peach' }
/** Owner, 2026-10-18: a plain tmux terminal on a machine — the menu's last row. */
const TERMINAL_ROW = { label: 'Terminal', icon: 'terminal' as const, color: 'teal' }
/** Owner, 2026-10-18: 在 + Plus 這個按鈕下面再加入一個新的讓使用者可以創建 Group 的功能. */
const GROUP_ROW = { label: 'Group', icon: 'groups' as const, color: 'yellow' }
/** Owner, 2026-10-19: 新增一個遊戲的欄位, 使用這個點進去之後可以打開他想要玩的遊戲 (games/GamesSection). */
const GAME_ROW = { label: 'Game', icon: 'sports_esports' as const, color: 'green' }

/** The menu's rows below Add a new machine, in the owner's order — Terminal last (2026-10-18:
 *  我們把 Terminal 這一個欄位挪到最下方). */
const ROW_ORDER: View[] = ['agent', 'project', 'humans', 'group', 'game', 'terminal']

/**
 * The + button's sheet: start a new agent, pair a machine, make a project, add a human.
 *
 * SCAN/ATTACH IS GONE, by the owner's decision (2026-08-04): the flow the product
 * wants is "start one", not "adopt what is already running". The BFF still exposes
 * scan/detected/attach — nothing here calls them any more, and if adoption returns
 * it returns as a decision, not as leftover UI. (Recorded because removed-on-purpose
 * and never-wired must stay distinguishable in this codebase.)
 *
 * Creation is a QUEUE, and the screen says so: the edge is pure-outbound, so the
 * cloud parks the command and the machine collects it on its next beat (≤30s).
 */
export function StartAgentSheet({
  server,
  onCreated,
  onProjectCreated,
  onStatus,
  onClose,
  netMs,
  selfEmail = null,
  onScanOpen,
  onOpenShell,
  onTerminalCreated,
  onGroupCreated,
}: {
  /**
   * The machine to start on, when the caller has already chosen one (Workspace).
   *
   * OMIT IT and this sheet asks for the machine itself — the ＋ button's path: the
   * owner's instruction was one screen — server, folder, name, type, create — instead
   * of a "Choose a server" dialog followed by a "Start a new agent on X" dialog followed
   * by a directory browser that replaced the form. Their words: 要分多次按鈕，很麻煩.
   */
  server?: PairedServer | undefined
  /** `deviceId` so the caller can file the new session against the right machine
   *  without having chosen it — the sheet may have chosen it. */
  onCreated: (session: string, deviceId: string, where: string | null) => void
  /*
   * The Project row — owner, 2026-08-08: 沒看到分頁,重啟也無效.
   *
   * Project creation lived in NewAgentSheet's Project tab, and when this sheet
   * replaced NewAgentSheet (the one-screen rework) nothing imported it any more:
   * every line of the feature present, none of it reachable — the same
   * both-ends-green-no-middle shape as ?shellBuild= and the upload lanes. It lives
   * HERE, in the sheet the ＋ actually opens. Optional so the Workspace-preset path
   * ("start on X") stays a single-purpose form.
   */
  onProjectCreated?: ((p: ProjectRow) => void) | undefined
  /** Attach failures are reported, not silenced — the project exists either way. */
  onStatus?: ((msg: string) => void) | undefined
  onClose: () => void
  /** Injectable for tests. */
  netMs?: number | undefined
  /** The signed-in account's email, for the Humans view's "my code". */
  selfEmail?: string | null
  /** Told when the pairing camera takes the screen, so App can pause marking-read
   *  behind it — the scanner lives in the Add a new machine view. */
  onScanOpen?: ((open: boolean) => void) | undefined
  /** Open a shell on a machine with a command pre-typed — the tmux-install fallback. */
  onOpenShell?: ((device: string, name: string, command: string) => void) | undefined
  /** ＋ → Terminal made one (CreateTerminalSection); the row shows only where this is given. */
  onTerminalCreated?: ((session: string, deviceId: string, path: string) => void) | undefined
  /** ＋ → Group made one (CreateGroupSection); the row shows only where this is given. */
  onGroupCreated?: ((gid: string, name: string) => void) | undefined
}) {
  /** Only used when no server was handed in. */
  const [picked, setPicked] = useState<PairedServer | null>(null)
  const chosen = server ?? picked
  const [view, setView] = useState<View>('menu')
  /*
   * THE MACHINE LIST, asked here once and handed to the picker so it is not fetched twice
   * for two decisions: whether Agent can be chosen at all (an account with nothing paired
   * answers `kind: 'none'` — owner, 2026-09-13: 使用者第一次登入, 沒有任何機器配對), and
   * the picker's rows. Asked again on the way back from Add a new machine, where one may
   * just have been paired. An error or a signed-out answer leaves Agent open: the picker
   * is where that state is explained.
   */
  const [list, setList] = useState<ServerList | null>(null)
  const [asked, setAsked] = useState(0)
  useEffect(() => {
    if (server) return
    let alive = true
    void fetchServers().then((l) => {
      if (alive) setList(l)
    })
    return () => {
      alive = false
    }
  }, [server, asked])

  // A preset machine (Workspace's "start on X") asked for exactly one thing: the form.
  if (server) {
    return (
      <Sheet title={`Start a new agent on ${server.name}`} onClose={onClose}>
        <CreateAgentSection
          server={server}
          netMs={netMs}
          onDone={(sess, where) => onCreated(sess, server.server_id, where)}
          {...(onOpenShell ? { onOpenShell } : {})}
          showHeading
        />
      </Sheet>
    )
  }

  const noMachine = list?.kind === 'none'
  // Project and Humans only where the caller can act on a project (the ＋ path does).
  const tiles = LAUNCHPAD_TILES.filter((t) => t.key === 'agent' || onProjectCreated)
  const labelOf = (v: View): string =>
    v === 'machine'
      ? MACHINE_ROW.label
      : v === 'terminal'
        ? TERMINAL_ROW.label
        : v === 'group'
          ? GROUP_ROW.label
          : v === 'game'
            ? GAME_ROW.label
            : (LAUNCHPAD_TILES.find((t) => t.key === v)?.label ?? '')
  const back = () => {
    if (view === 'machine') setAsked((n) => n + 1)
    setView('menu')
  }

  return (
    <Sheet
      title="New"
      onClose={onClose}
      // A column of fields rather than a list: at the default ceiling the type and the
      // button fall below the fold. The menu itself is four rows and needs no height.
      // Project's agent list takes the room its fields leave (owner, 2026-10-28; .sheet-fill).
      {...(view === 'menu' ? {} : { className: view === 'project' ? 'sheet-tall sheet-fill' : 'sheet-tall' })}
    >
      {view === 'menu' ? (
        <div className="ac-menu">
          <button type="button" className="ac-row" onClick={() => setView('machine')}>
            <Icon name={MACHINE_ROW.icon} size={18} className={`lp-ink-${MACHINE_ROW.color}`} />
            <span className="ac-row-label">{MACHINE_ROW.label}</span>
            <Icon name="chevron_right" size={18} className="ac-row-go" />
          </button>
          {[
            ...tiles,
            // Terminal, last (ROW_ORDER): like Agent, something a machine runs, so it needs one.
            ...(onTerminalCreated ? [{ key: 'terminal' as const, ...TERMINAL_ROW }] : []),
            ...(onGroupCreated ? [{ key: 'group' as const, ...GROUP_ROW }] : []),
            // Where the ＋ is the app's own (onProjectCreated marks that path): a game is no machine's.
            ...(onProjectCreated && gamesOn() ? [{ key: 'game' as const, ...GAME_ROW }] : []),
          ]
            .sort((a, b) => ROW_ORDER.indexOf(a.key) - ROW_ORDER.indexOf(b.key))
            .map((t) => {
              const off = (t.key === 'agent' || t.key === 'terminal') && noMachine
              return (
                <button
                  key={t.key}
                  type="button"
                  className="ac-row"
                  disabled={off}
                  onClick={() => setView(t.key)}
                >
                  <Icon name={t.icon} size={18} className={`lp-ink-${t.color}`} />
                  <span className="ac-row-label">{t.label}</span>
                  {/* Why it cannot be chosen, in the row itself: the next step is the row above. */}
                  {off && <span className="ac-row-val">No machine yet</span>}
                  <Icon name="chevron_right" size={18} className="ac-row-go" />
                </button>
              )
            })}
        </div>
      ) : (
        <>
          {/* Game draws its own back row: it is several screens deep, and two back rows read as a riddle. */}
          {view !== 'game' && (
            <button type="button" className="ac-back" aria-label="Back" onClick={back}>
              <Icon name="chevron_left" size={18} />
              {labelOf(view)}
            </button>
          )}
          {view === 'machine' && <InstallSection bare {...(onScanOpen ? { onScanOpen } : {})} />}
          {view === 'project' && onProjectCreated && (
            <CreateProjectSection onCreated={onProjectCreated} onStatus={onStatus ?? (() => {})} />
          )}
          {view === 'humans' && <AddHumans selfEmail={selfEmail} onStatus={onStatus ?? (() => {})} />}
          {view === 'terminal' && onTerminalCreated && <CreateTerminalSection onDone={onTerminalCreated} />}
          {view === 'group' && onGroupCreated && <CreateGroupSection onCreated={onGroupCreated} />}
          {/* Opening a game closes the sheet: the game takes the screen (games/GameHost). */}
          {view === 'game' && <GamesSection onOpened={onClose} onBack={back} />}
          {view === 'agent' && (
            <>
              <ServerField list={list} picked={picked} onPick={setPicked} onClear={() => setPicked(null)} />
              {chosen ? (
                <CreateAgentSection
                  server={chosen}
                  netMs={netMs}
                  onDone={(sess, where) => onCreated(sess, chosen.server_id, where)}
                  {...(onOpenShell ? { onOpenShell } : {})}
                  showHeading={false}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </Sheet>
  )
}

/**
 * The project half, ported MECHANISM-VERBATIM from NewAgentSheet (the orphaned
 * sheet this one replaced): the attach-after-create with failure reporting, the
 * always-say-the-total counter, the keep-ticked-visible filter. No group step
 * anywhere — the router files a member-less creation under the caller's own solo
 * namespace, so the project simply exists and can be shared later.
 */
